import React, { useEffect, useRef, useState } from "react";
import { Mic, Trash2, SendHorizontal, Loader2 } from "lucide-react";

/** Formats WhatsApp accepts for audio, in order of preference (Opus in OGG is a real "voice note") */
const FORMATS = [
  { mime: "audio/ogg;codecs=opus", ext: "ogg" },
  { mime: "audio/mp4", ext: "m4a" },
  { mime: "audio/mp4;codecs=mp4a.40.2", ext: "m4a" },
];

export const pickRecordingFormat = () =>
  typeof MediaRecorder === "undefined" ? null : FORMATS.find((f) => MediaRecorder.isTypeSupported(f.mime)) || null;

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

interface VoiceRecorderProps {
  /** Called with the finished recording; resolve true when sent */
  onSend: (file: File) => Promise<boolean>;
  onError: (message: string) => void;
  disabled?: boolean;
}

/** Mic button → recording bar with timer, cancel and send (like WhatsApp) */
export function VoiceRecorder({ onSend, onError, disabled }: VoiceRecorderProps) {
  const [state, setState] = useState<"idle" | "recording" | "sending">("idle");
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelled = useRef(false);
  const format = pickRecordingFormat();

  const stopTracks = () => recorder.current?.stream.getTracks().forEach((t) => t.stop());

  useEffect(
    () => () => {
      if (timer.current) clearInterval(timer.current);
      stopTracks();
    },
    []
  );

  const start = async () => {
    if (!format) return onError("This browser can't record audio in a format WhatsApp accepts. Try Chrome 126+, Firefox or Safari.");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: format.mime });
      chunks.current = [];
      cancelled.current = false;
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      rec.onstop = async () => {
        stopTracks();
        if (timer.current) clearInterval(timer.current);
        if (cancelled.current || !chunks.current.length) return setState("idle");
        setState("sending");
        const blob = new Blob(chunks.current, { type: format.mime.split(";")[0] });
        const file = new File([blob], `voice-note-${Date.now()}.${format.ext}`, { type: format.mime.split(";")[0] });
        await onSend(file);
        setState("idle");
      };
      recorder.current = rec;
      rec.start();
      setSeconds(0);
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      setState("recording");
    } catch {
      onError("Microphone access was blocked. Allow it in the browser to record a voice note.");
    }
  };

  const stop = (cancel: boolean) => {
    cancelled.current = cancel;
    recorder.current?.state === "recording" && recorder.current.stop();
  };

  if (state === "idle") {
    return (
      <button
        type="button"
        onClick={start}
        disabled={disabled}
        title="Record voice note"
        aria-label="Record voice note"
        className="size-10 shrink-0 rounded-full flex items-center justify-center text-white disabled:opacity-60"
        style={{ background: "var(--wa-green)" }}
      >
        <Mic className="size-5" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 shrink-0">
      {state === "recording" ? (
        <>
          <button type="button" onClick={() => stop(true)} title="Cancel" aria-label="Cancel recording" className="size-9 rounded-full flex items-center justify-center" style={{ color: "var(--wa-muted)" }}>
            <Trash2 className="size-5" />
          </button>
          <span className="flex items-center gap-1.5 text-[14px] tabular-nums" style={{ color: "var(--wa-text)" }}>
            <span className="size-2.5 rounded-full bg-[#ea0038] animate-pulse" />
            {fmt(seconds)}
          </span>
          <button type="button" onClick={() => stop(false)} title="Stop and send" aria-label="Stop and send" className="size-10 rounded-full flex items-center justify-center text-white" style={{ background: "var(--wa-green)" }}>
            <SendHorizontal className="size-5" />
          </button>
        </>
      ) : (
        <span className="size-10 rounded-full flex items-center justify-center text-white" style={{ background: "var(--wa-green)" }}>
          <Loader2 className="size-5 animate-spin" />
        </span>
      )}
    </div>
  );
}

